import React, { useMemo, useRef, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    Typography,
} from "@mzc-pdc/ui";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { IconCancel, IconClose, IconErrorCircle } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { useTranslation } from "react-i18next";
import AutocompleteSmallTableList from "@features/autocomplete-table/autocomplete-small-table-list";
import { getTableValueByAssetColumn } from "@features/autocomplete-table/autocomplete-table-columns-component/autocomplete-table-columns-component";
import { useDispatch, useSelector } from "react-redux";
import Tooltip from "@components_v2/Tooltip/tooltip";
import { setNotification } from "@modules/notification";
import { useAutocompleteTable } from "@hooks/useAutocompleteTable";
import { getAssetsAPI } from "@modules/apis/assets";
import { addAssetLinkedAssetsAPI } from "@modules/apis/asset";
import SkeletonSearchedAssetsTable from "@features/autocomplete-table/autocomplete-table-skeleton/autocomplete-table-popper-skeleton-searched-assets";
import { SearchedAssetColumn } from "@features/autocomplete-table/autocomplete-table-columns/autocomplete-table-columns-assets";
import AutocompleteFixedTable from "@features/autocomplete-table/autocomplete-fixed-table";

const DialogAddLinkedAssetsV2 = ({ excludeItemIds, onClose, onSubmit }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const MAX_SELECTED_ASSETS = 50;

    const { getSmallTableColumns, getSubNameIds } = useAutocompleteTable();

    const [selectedResources, setSelectedResources] = useState([]);
    const [actionPending, setActionPending] = useState(false);

    const LIMIT = 20;
    const [results, setResults] = useState([]);
    const [pending, setPending] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const offsetRef = useRef(0);

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);

    const getResourceAssetData = async (params) => {
        try {
            const { searchKeyowrd, ...restParams } = params;
            let response = null;
            if (pending) return;
            setPending(true);

            const preResults = offsetRef.current > 0 ? results : [];

            let assetParams = {
                generateAccessUrl: true,
                limit: LIMIT,
                offset: offsetRef.current,
            };
            if (params.searchKeyword) assetParams = { ...assetParams, name: params.searchKeyword, ...restParams };
            else assetParams = { ...assetParams, ...params };
            response = await getAssetsAPI(stage.id, stage.endpoint, project.id, assetParams);
            offsetRef.current = offsetRef.current + LIMIT;

            setResults([...preResults, ...(response.data?.assets || [])]);
            setTotalCount(response.data?.totalCount);
            return [...preResults, ...(response.data?.assets || [])];
        } catch (e) {
            console.error(e);
        } finally {
            setPending(false);
        }
    };

    const resetParams = () => {
        setTotalCount(0);
        setResults([]);
        offsetRef.current = 0;
    };

    const onHandleSearchData = async (search) => {
        await resetParams();
        await getResourceAssetData(search);
    };

    const onHandleSubmitResources = async () => {
        try {
            setActionPending(true);

            // await addAssetLinkedAssetsAPI(stage.endpoint, project.id, assetId, {
            //     assetIds: selectedResources.map((el) => el.id),
            // });

            await onSubmit(selectedResources);

            onClose();
        } catch (e) {
            console.log(e);
            dispatch(
                setNotification({
                    type: "error",
                    contents: e.response
                        ? `[Error:${e.response.status}] => ${e.response?.data?.message}`
                        : `Internal Error Occured`,
                }),
            );
            setActionPending(false);
        }
    };

    const onHandleSelectResources = async (checkedList) => {
        setSelectedResources(checkedList);
    };

    const getFilterList = useMemo(() => {
        return [{ id: "mediaType", label: null, title: "Media type", value: null, type: "types" }];
    }, []);

    const onHandleDeleteResources = (id) => {
        const newResources = selectedResources.filter((el) => el.id !== id);
        setSelectedResources(newResources);
    };

    return (
        <Dialog
            onClose={() => onClose()}
            open={true}
            sx={{
                ".MuiDialog-container >.MuiPaper-root": {
                    minWidth: "1340px",
                },
                ".MuiDialogContent-root": {
                    height: "808px",
                    scrollbarGutter: "unset",
                },
            }}
        >
            <DialogTitle>
                <Grid item>
                    <Typography variant="h3">{t(`common::label::Add Assets`, `Add Assets`)}</Typography>
                </Grid>
                <TooltipButton
                    circled
                    title={t(`common::label::Close`)}
                    onClickButton={onClose}
                    buttonIcon={<IconClose size={20} htmlColor={theme.palette.secondary[700]} />}
                />
            </DialogTitle>
            <DialogContent>
                <Grid
                    container
                    flexDirection={"row"}
                    width={"100%"}
                    height={"100%"}
                    borderTop={`1px solid ${theme.palette.greySecondary[100]}`}
                    gap={2.5}
                    pt={2.5}
                >
                    <Grid item flex={1}>
                        <AutocompleteFixedTable
                            domain={`Asset`}
                            type={"table"}
                            key={"autocomplete-add-linked-assets-table"}
                            placement={"bottom-end"}
                            placeholder={t(`common::msg::Please search by name.`, `Please search by name.`)}
                            onSubmit={onHandleSelectResources}
                            getValueByColumn={getTableValueByAssetColumn}
                            onDebounceSearch={(search) => onHandleSearchData(search)}
                            onFetchData={getResourceAssetData}
                            columns={SearchedAssetColumn}
                            skeletonColumns={<SkeletonSearchedAssetsTable columns={SearchedAssetColumn} />}
                            viewMore={results.length < totalCount}
                            options={results.filter((el) => el.state !== "DRAFT" && !excludeItemIds?.includes(el.id))}
                            isPending={pending}
                            selectedItems={selectedResources}
                        />
                    </Grid>
                    <Divider orientation={"vertical"} flexItem />
                    <Grid
                        item
                        width={420}
                        sx={{ display: `flex`, flexDirection: `column`, overflow: `hidden`, gap: 2 }}
                    >
                        <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                            <Typography variant={"body1"} color={theme.palette.secondary[800]}>
                                {t(`common::label::Selected Assets`, `Selected Asset`)} (
                                <Typography
                                    variant={"body1"}
                                    color={
                                        selectedResources.length > MAX_SELECTED_ASSETS
                                            ? theme.palette.error[400]
                                            : theme.palette.primary[400]
                                    }
                                >
                                    {selectedResources.length}
                                </Typography>
                                /{MAX_SELECTED_ASSETS})
                            </Typography>
                            {selectedResources.length > MAX_SELECTED_ASSETS && (
                                <IconErrorCircle size={16} htmlColor={theme.palette.error.main} />
                            )}
                        </Stack>
                        <Grid item flex={1}>
                            <AutocompleteSmallTableList
                                type={"asset"}
                                data={selectedResources}
                                columns={getSmallTableColumns("ASSET")}
                                subNameIds={getSubNameIds("ASSET")}
                                deleteButton={
                                    <Tooltip title={t(`common::label::Delete`)}>
                                        <IconButton
                                            size="medium"
                                            color={"inherit"}
                                            circled
                                            onClick={(e) => {
                                                onHandleDeleteResources(e.target.closest("tr").dataset.id);
                                            }}
                                        >
                                            <IconCancel size={12} htmlColor={theme.palette.grey[700]} />
                                        </IconButton>
                                    </Tooltip>
                                }
                                noDataText={t(`common::msg::Select assets.`, `Select assets.`)}
                                divider={true}
                                gridStyle={{ maxHeight: "670px" }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ borderTop: `1px solid ${theme.palette.greySecondary[100]}`, p: 3 }}>
                <Button
                    color="secondary"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    variant="text"
                >
                    {t(`common::label::${"Cancel"}`)}
                </Button>
                <Button
                    color="primary"
                    onClick={() => onHandleSubmitResources()}
                    disabled={
                        selectedResources?.length === 0 ||
                        actionPending ||
                        selectedResources?.length > MAX_SELECTED_ASSETS
                    }
                    loading={actionPending}
                    variant="contained"
                >
                    {t(`common::label::${"Add"}`)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DialogAddLinkedAssetsV2;
